import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ROWS THAT BELONG TO NOBODY — migration 0058, from the Admin Diagnostics page.
 *
 * A row with no company_id can be served to anybody. Today everything is one
 * firm's, so nothing has leaked out; the day a second tenant logs in, every one
 * of these is visible to them.
 *
 * Which company owns a legacy row is a judgement, not a guess — so the company
 * is chosen here and the word APPLY has to be typed. The GET is a dry run and
 * writes nothing, ever.
 */
export const UnownedRowsCard = () => {
  const [data, setData] = useState(null);
  const [company, setCompany] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/tenancy/unowned`, { timeout: 180000 });
      setData(r.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || 'could not run the dry run' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/tenancy/unowned`,
        { company_id: company, confirm }, { timeout: 300000 });
      toast.success(`${r.data.rows_stamped} row(s) stamped across ${r.data.collections} collections`);
      setConfirm('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'nothing was written');
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <div className="p-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-500"
                data-testid="unowned-loading">Counting rows with no owner…</div>;
  }
  if (data.error) {
    return <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-sm text-red-800"
                data-testid="unowned-error">{data.error}</div>;
  }

  const clean = data.unowned_rows === 0;
  const top = [...(data.detail || [])].sort((a, b) => b.unowned - a.unowned).slice(0, 8);

  return (
    <div className={`p-5 rounded-xl border ${clean ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}
         data-testid="unowned-card">
      <h3 className="font-semibold text-slate-900">Rows that belong to nobody</h3>
      <p className="text-xs text-slate-600 mt-0.5 mb-3">
        A row with no <code>company_id</code> can be served to anybody. The read filters
        still allow them on purpose — a strict filter would hide this firm's own history.
        Stamp them, then the boundary can be made hard.
      </p>

      <p className={`text-sm font-medium mb-3 ${clean ? 'text-emerald-800' : 'text-red-800'}`}
         data-testid="unowned-total">
        {clean ? 'None. Every row has an owner.'
               : `${data.unowned_rows.toLocaleString()} unowned row(s) across ${data.collections} collections`}
      </p>

      {!clean && (
        <>
          <table className="text-xs w-full mb-4" data-testid="unowned-table">
            <thead className="text-slate-500">
              <tr><th className="text-left font-normal">collection</th>
                  <th className="text-right font-normal">rows</th>
                  <th className="text-right font-normal">no owner</th></tr>
            </thead>
            <tbody className="font-mono">
              {top.map((r) => (
                <tr key={r.collection}>
                  <td className="text-slate-800">{r.collection}</td>
                  <td className="text-right text-slate-600">{r.rows}</td>
                  <td className="text-right text-red-700">{r.unowned}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-wrap items-center gap-2">
            <select value={company} onChange={(e) => setCompany(e.target.value)}
                    data-testid="unowned-company"
                    className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white">
              <option value="">Which company owns them?</option>
              {(data.companies || []).map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {c.projects} projects</option>
              ))}
            </select>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   placeholder="type APPLY" data-testid="unowned-confirm"
                   className="text-xs px-2 py-1.5 rounded border border-slate-300 bg-white font-mono w-28" />
            <button type="button" onClick={apply}
                    disabled={busy || !company || confirm !== 'APPLY'}
                    data-testid="unowned-apply"
                    className="px-3 py-1.5 text-xs rounded-md bg-slate-900 text-white disabled:opacity-40">
              {busy ? 'Stamping…' : 'Apply migration 0058'}
            </button>
            <span className="text-xs text-slate-500">Dry run above wrote nothing.</span>
          </div>
        </>
      )}
    </div>
  );
};

export default UnownedRowsCard;
